import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

interface SubscriptionStatus {
  subscribed: boolean;
  subscription_tier: string | null;
  subscription_end: string | null;
  is_free: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  subscription: SubscriptionStatus;
  checkSubscription: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionStatus>({
    subscribed: false,
    subscription_tier: null,
    subscription_end: null,
    is_free: false,
  });

  const checkSubscription = async () => {
    if (!session?.access_token) return;

    // Invoke with the current token; on 401 (expired token) refresh the
    // session once and retry, so paying members aren't briefly shown as
    // unsubscribed when their access token lapses.
    const invokeWithToken = async (token: string) =>
      supabase.functions.invoke("check-subscription", {
        headers: { Authorization: `Bearer ${token}` },
      });

    try {
      let activeToken = session.access_token;
      let { data, error } = await invokeWithToken(activeToken);

      if (error) {
        const status = (error as { context?: { status?: number } })?.context?.status;
        if (status === 401) {
          const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
          if (refreshError || !refreshed.session?.access_token) {
            console.warn("[AuthContext] Session refresh failed after 401; user may need to log in again");
            return;
          }
          activeToken = refreshed.session.access_token;
          ({ data, error } = await invokeWithToken(activeToken));
          if (error) {
            console.error("Error checking subscription after token refresh:", error);
            return;
          }
        } else {
          console.error("Error checking subscription:", error);
          return;
        }
      }

      // SELF-HEAL: If user has Stripe customer but no subscription record, trigger sync
      if (data.needs_sync && data.stripe_customer_id) {
        console.log("[AuthContext] Detected missing subscription - triggering self-heal");
        
        try {
          const { error: syncError } = await supabase.functions.invoke("refresh-my-subscription", {
            headers: {
              Authorization: `Bearer ${activeToken}`,
            },
          });
          
          if (!syncError) {
            // Re-check subscription after sync
            const { data: refreshedData } = await supabase.functions.invoke("check-subscription", {
              headers: {
                Authorization: `Bearer ${activeToken}`,
              },
            });
            
            if (refreshedData) {
              setSubscription({
                subscribed: refreshedData.subscribed || false,
                subscription_tier: refreshedData.subscription_tier || null,
                subscription_end: refreshedData.subscription_end || null,
                is_free: refreshedData.is_free || false,
              });
              console.log("[AuthContext] Self-heal successful", refreshedData);
              return;
            }
          }
        } catch (syncErr) {
          console.error("[AuthContext] Self-heal failed:", syncErr);
        }
      }

      setSubscription({
        subscribed: data.subscribed || false,
        subscription_tier: data.subscription_tier || null,
        subscription_end: data.subscription_end || null,
        is_free: data.is_free || false,
      });
    } catch (error) {
      console.error("Error checking subscription:", error);
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setSubscription({
      subscribed: false,
      subscription_tier: null,
      subscription_end: null,
      is_free: false,
    });
  };

  useEffect(() => {
    // Listen for auth changes FIRST (prevents missing events during init)
    const {
      data: { subscription: authSubscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      // IMPORTANT: keep this callback synchronous (no await) to avoid deadlocks
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // THEN get initial session
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });

    return () => authSubscription.unsubscribe();
  }, []);

  // Check subscription when session changes
  useEffect(() => {
    if (session) {
      checkSubscription();
    }
  }, [session]);

  // Keep the denormalised caregiver_profiles.email in sync with the confirmed
  // auth email. Runs whenever the signed-in email changes (i.e. right after a
  // user confirms an email change via the link we mailed them).
  useEffect(() => {
    if (!user?.email) return;
    (async () => {
      try {
        await (supabase as any).rpc("sync_own_caregiver_email");
      } catch (err) {
        console.warn("[AuthContext] email sync failed", err);
      }
    })();
  }, [user?.id, user?.email]);

  // Event-driven subscription refresh (replaces 60s polling).
  // Refetches only when:
  //  - tab becomes visible AND >10 min since last check
  //  - Supabase auth token is refreshed
  // BetalingSucces page calls checkSubscription() directly after checkout.
  useEffect(() => {
    if (!session) return;
    let lastCheck = Date.now();
    const TEN_MIN = 10 * 60 * 1000;

    const onVisibility = () => {
      if (document.visibilityState === "visible" && Date.now() - lastCheck > TEN_MIN) {
        lastCheck = Date.now();
        checkSubscription();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "TOKEN_REFRESHED") {
        lastCheck = Date.now();
        checkSubscription();
      }
    });

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      authSub.unsubscribe();
    };
  }, [session]);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        subscription,
        checkSubscription,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
